import moment, { Moment } from "moment-timezone";
import { useTranslation } from "react-i18next";
import TabsComponent from "../../../components/tabs/TabsComponent";
import HoursGrid from "../../../components/candidate-calendar/HoursGrid";
import { useEffect, useState } from "react";
import { getHoursList } from "../../../services/schdeuleTimeOptionsService";
import { Hash } from "../../../api/@types/apiTypes";
import ContainerMd from "../../../components/Layouts/ContainerMd";
import LogoWithLanguage from "../../../components/logo-with-language/LogoWithLanguage";
import DateHeaderContainerSlider from "../../../components/candidate-calendar/DateHeaderContainerSlider";
import { useNavigate, useParams } from "react-router-dom";
import { makeApiRequest } from "../../../helpers";
import { ApiResponse } from "../../../api/apiResponse";
import ApiDateParser from "../../../helpers/parsers/apiDateParser";
import { availableSession } from "../../../api/endpoints/learner/availableSlots";
import { bookingSession } from "../../../api/endpoints/learner/calendarBooking";
import toast from "react-hot-toast";
import LearnerAvailableSlotsModel from "../../../api/models/LearnerAvailableSlotsModel";
import { isBeforeTime, isSameOrAfterTime } from "../../../helpers/date_helpers";
import TimeZoneAlert from "../../../components/alerts/timeZone";

const DATE_FORMAT = "D/M/YYYY";
const HOURS_STEP = 20; //

type SectionSlot = {
  isDisabled: boolean;
  dateTime: Moment;
};

export default function CalendarBookingPage() {
  const navigate = useNavigate();
  const { learner_token = "" } = useParams();
  const { t } = useTranslation("", {
    keyPrefix: "page.candidate.dashboard.bookSession",
  });

  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [selectedTime, setSelectedTime] = useState<Moment | null>(null);
  const [availableSlots, setAvailableSlots] = useState<Hash<Moment[]>>({});
  const [amHoursList, setAMHoursList] = useState<SectionSlot[]>([]);
  const [pmHoursList, setPMHoursList] = useState<SectionSlot[]>([]);

  useEffect(() => {
    if (!!selectedDate) populateHoursForDate(selectedDate);
  }, [selectedDate, availableSlots]);

  useEffect(() => {
    fetchAvailableSlots();
  }, []);

  const handleSelectDate = (date: Moment | null) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleSelectTime = (time: Moment | null) => {
    setSelectedTime(time);
  };

  const populateHoursForDate = (date: Moment) => {
    // If the availableSlots have not been set yet then this method shouldn't run
    if (!availableSlots[date.format("YYYY-MM-DD")]) return;

    // We calculate the offset needed on the 20 minutes slots for timezones that have fractions in them like +330
    const timezoneOffset: number = new Date().getTimezoneOffset();
    const minutesOffset = Math.abs(timezoneOffset % HOURS_STEP);
    //Generate am/pm time slots
    // We add the minutes offset according to the timezone and we start the dates generation with the offseted date in order to
    // match the slots returned from the backend
    const amStart = date
      .clone()
      .local()
      .set({ hours: 0, minutes: minutesOffset, seconds: 0, millisecond: 0 });

    const TOTAL_HOURS_IN_A_DAY = 24;
    const TOTAL_MINUTES_IN_HOUR = 60;
    const numberOfSlots =
      (TOTAL_HOURS_IN_A_DAY * TOTAL_MINUTES_IN_HOUR) / HOURS_STEP;

    // We generate the list of hours starting from the offseted date amStart
    const hoursList = getHoursList(amStart, numberOfSlots, HOURS_STEP).map(
      (time) => hourMapper(time, date)
    );

    // We filter the generated hours into two lists (AM and PM) according to the cutOff date amCutOff
    const amCutOff = date
      .clone()
      .local()
      .set({ hours: 12, minutes: 0, seconds: 0, millisecond: 0 });
    const generatedAMHoursList = hoursList.filter((t) =>
      isBeforeTime(t.dateTime, amCutOff)
    );
    const generatedPMHoursList = hoursList.filter((t) =>
      isSameOrAfterTime(t.dateTime, amCutOff)
    );
    setAMHoursList(generatedAMHoursList);
    setPMHoursList(generatedPMHoursList);
  };

  const fetchAvailableSlots = async () => {
    const todayUTC = moment().utc();
    const startDate = todayUTC.format(DATE_FORMAT);
    const endDate = todayUTC.clone().add(7, "days").format(DATE_FORMAT);
    await makeApiRequest(
      () => availableSession(startDate, endDate, learner_token),
      (response: ApiResponse) => {
        const learnerAvailableSlots =
          LearnerAvailableSlotsModel.fromApiResponse(response);
        const dateFormat = "YYYY-MM-DD";
        const slots = learnerAvailableSlots.toLocalSlots();

        // filter out times before now
        const today = moment().format(dateFormat);
        if (today in slots) {
          slots[today] = slots[today].filter((time) => time.isAfter(moment()));
        }

        setAvailableSlots(slots);
        const firstAvailableDate = Object.values(slots)[0][0];
        handleSelectDate(firstAvailableDate);
      }
    );
  };

  // Generating week days starting from Today
  const startOfTheWeek = moment();
  const dates = [...Array(7)].map((_, i) => {
    const date = startOfTheWeek.clone().add(i, "days");
    return {
      date: startOfTheWeek.clone().add(i, "day"),
      isDisabled: !availableSlots[date.format("YYYY-MM-DD")]?.length,
    };
  });

  const hourMapper = (hour: Moment, date: Moment) => {
    // Disable hour that is not available
    const daySlots = availableSlots[date.format("YYYY-MM-DD")];
    const slotIndex = daySlots.findIndex((time) => {
      return (
        time.clone().local().format("HH:mm") ===
        hour.clone().local().format("HH:mm")
      );
    });
    const isDisabled = slotIndex < 0;
    return {
      dateTime: hour,
      isDisabled,
    };
  };

  const handleBookSession = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date to continue");
      return;
    }

    const selectedDateUTC: Moment = selectedDate!.clone().utc();
    const selectedTimeUTC: Moment = selectedTime!.clone().utc();
    const date = selectedDateUTC.format(DATE_FORMAT);
    const day = selectedDateUTC.locale("en")?.format("dddd");
    const timeSeconds = selectedTimeUTC!.format("X");
    const start_time = ApiDateParser.toNormalizedApiDate(selectedTimeUTC!);
    const end_time = ApiDateParser.toNormalizedApiDate(
      selectedTimeUTC!.clone().add(HOURS_STEP, "minutes")!
    );

    await makeApiRequest(
      () =>
        bookingSession(
          {
            is_active: true,
            schedule_slots_attributes: [
              {
                day_of_the_week: day?.toLowerCase(),
                date,
                start_time,
                end_time,
              },
            ],
          },
          learner_token,
          date + "",
          timeSeconds
        ),
      (response: ApiResponse) => {
        if (response.isSuccess) {
          navigate("/learner/success-booking-session");
        }
      }
    );
  };

  const timeTabs = [
    {
      name: t("am"),
      content: (
        <HoursGrid
          hoursList={amHoursList}
          onSelectTime={handleSelectTime}
          selectedTime={selectedTime}
        />
      ),
    },
    {
      name: t("pm"),
      content: (
        <HoursGrid
          hoursList={pmHoursList}
          onSelectTime={handleSelectTime}
          selectedTime={selectedTime}
        />
      ),
    },
  ];

  return (
    <ContainerMd>
      <div className="flex flex-col justify-between w-full h-screen">
        <div className="w-full">
          <LogoWithLanguage />
          <div className="w-full mt-8 ">
            <div className="my-4">
              <TimeZoneAlert />
            </div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("sessionDate")}
            </div>

            <DateHeaderContainerSlider
              datesList={dates}
              onSelectDay={handleSelectDate}
              selectedDate={selectedDate}
              className="flex flex-row w-full p-2 overflow-x-auto flex-nowrap"
            />

            <div className="mt-6 mb-2 text-sm font-medium text-gray-700">
              {t("sessionTime")}
            </div>
            <div className="w-full">
              <TabsComponent tabs={timeTabs} showDropDownOnMobile={false} />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 z-20 flex items-center justify-end w-full py-5 bg-white border-t border-gray-200">
          <button
            onClick={handleBookSession}
            className=" w-full  max-w-[72px] py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
          >
            {t(`send`)}
          </button>
        </div>
      </div>
    </ContainerMd>
  );
}
